export interface ApiErrorPayload {
  code: string;
  message: string;
  httpStatus: number;
  details?: unknown;
}

export class ApiError extends Error {
  readonly code: string;
  readonly httpStatus: number;
  readonly details: unknown;
  constructor(payload: ApiErrorPayload) {
    super(payload.message);
    this.name = "ApiError";
    this.code = payload.code;
    this.httpStatus = payload.httpStatus;
    this.details = payload.details;
  }
}

export function errorI18nKey(code: string): string {
  return `errors.${code}`;
}

// Backend SearchAbuseGuard signals "you must solve a Yandex SmartCaptcha and resend
// with X-Captcha-Token" by returning HTTP 200 + Result.error(SEARCH_CAPTCHA_REQUIRED)
// (= numeric code 4601). apiFetch surfaces that as ApiError("4601") — this matcher
// drives the captcha-retry wrapper.
export const CAPTCHA_REQUIRED_CODE = "4601";

export function isCaptchaRequired(error: unknown): boolean {
  return error instanceof ApiError && error.code === CAPTCHA_REQUIRED_CODE;
}

/**
 * Substrings the backend uses in auth-failure messages. Matched
 * case-insensitively so an expired/illegal token surfaces as "sign in again"
 * regardless of which layer rejected it:
 *  - Shiro filter rejects a malformed JWT → HTTP 401 ("Token is illegal and
 *    invalid!" / "Login expired, please log in again.").
 *  - A controller (e.g. /favorites/page, /order/listV2) rejects a token that
 *    parses but resolves to no user → Jeecg `Result.error("Login timeout!")`,
 *    which is HTTP 200 + envelope code 500 — NOT a 401 — so the status/code
 *    check below would miss it without this message match.
 */
const AUTH_ERROR_MESSAGE_PARTS = [
  "login timeout",
  "login expired",
  "token is illegal",
  "token is invalid",
  "please log in again",
] as const;

/**
 * True when the error is an authentication failure — the JWT is missing,
 * illegal or expired.
 *
 * Consumers (e.g. the favorites page, #101) use this to show a "session
 * expired — sign in again" prompt instead of a generic "load failed + retry",
 * because retrying with the same dead token can never succeed.
 */
export function isAuthError(error: unknown): boolean {
  if (error instanceof ApiError) {
    if (error.httpStatus === 401 || error.code === "401") return true;
  }
  if (error instanceof Error) {
    if (error.message === "NO_TOKEN") return true;
    const msg = error.message.toLowerCase();
    if (AUTH_ERROR_MESSAGE_PARTS.some((part) => msg.includes(part))) return true;
  }
  return false;
}
